'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { Icon } from '@/ui/icons/icon/Icon'
import type { ReactNode } from 'react'

export type ValidationMessageStatus = 'error' | 'warning' | 'info' | 'success' | 'default'

export type ValidationMessageProps = UiComponentProps<{
  // Drives the icon and the color
  status: ValidationMessageStatus
  children: ReactNode
}>

const statusConfig: Record<
  ValidationMessageStatus,
  { icon: keyof typeof Icon; colorClass: string }
> = {
  error: { icon: 'CircleX', colorClass: 'text-error-fg' },
  warning: { icon: 'CircleAlert', colorClass: 'text-warning-fg' },
  info: { icon: 'Info', colorClass: 'text-info-fg' },
  success: { icon: 'CircleCheck', colorClass: 'text-success-fg' },
  default: { icon: 'CircleCheck', colorClass: 'text-brand-2-700' },
}

export function ValidationMessage({
  status,
  children,
  className,
  id,
  'data-testid': dataTestId,
}: ValidationMessageProps) {
  // Do not render when children is empty or whitespace-only
  const text = typeof children === 'string' ? children : null

  if (text !== null && text.trim() === '') {
    return null
  }

  const { icon, colorClass } = statusConfig[status]
  const IconComponent = Icon[icon]

  return (
    <p
      id={id}
      data-testid={dataTestId ?? 'validation-message'}
      className={twMerge(
        className,
        'mt-0.5 inline-flex items-start gap-x-0.5 text-sm font-medium leading-lighter',
        colorClass,
      )}
    >
      <IconComponent size="sm" className={twMerge('shrink-0', colorClass)} aria-hidden />
      <span className="flex-1 whitespace-pre-line">{children}</span>
    </p>
  )
}
